<template>
  <div>
    <PreferenceConfigHeader
      v-model="display"
      :is-loading="isLoading"
      :preference-name="preferenceName"
      :preference-names="preferenceNames"
      :base-selector="baseSelector"
      @Update="update"
      @Remove="remove"
      @Copy="copy"
    />
    <div class="preference-config overflow-x">
      <table :class="display ? '' : 'visibility-collapse'">
        <WeightRow v-model="weightList" />
        <GroupRow
          v-for="(group, groupIndex) in groupsClone"
          :key="groupIndex"
          v-model="groupsClone[groupIndex]"
          :index="groupIndex"
          :preference-index="value.index"
          :weight-list="weightList"
          @Remove="removeGroup"
          @Copy="copyGroup"
        />
        <tr>
          <GroupCreateCell
            class="height-header-cell border-right-none"
            :is-loading="isLoading"
            :preference-index="value.index"
            @Add="addGroup"
          />
          <td
            v-for="(weight, weightIndex) in weightList"
            :key="weightIndex"
            class="height-cell border-right-none"
          />
          <td class="height-cell border-right-none" />
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import toast from '@/services/toast'
import PreferenceConfigHeader from '@/employees/components/PreferenceConfig/PreferenceConfigHeader'
import WeightRow from '@/employees/components/PreferenceConfig/Weight/WeightRow'
import GroupRow from '@/employees/components/PreferenceConfig/Group/GroupRow'
import GroupCreateCell from '@/employees/components/PreferenceConfig/Group/GroupCreateCell'
import _cloneDeep from 'lodash/cloneDeep'
export default {
  name: 'PreferenceConfig',
  components: {
    PreferenceConfigHeader,
    WeightRow,
    GroupRow,
    GroupCreateCell,
  },
  provide () {
    const selectorContext = {}
    Object.defineProperty(selectorContext, 'groupNames', {
      enumerable: true,
      get: () => this.groupNames,
    })
    return {
      selectorContext,
    }
  },
  props: {
    value: {
      required: true,
      type: Object,
    },
    isLoading: {
      required: true,
      type: Boolean,
    },
    preferenceName: {
      required: true,
      type: String,
    },
    preferenceNames: {
      required: true,
      type: Array,
    },
    groupNames: {
      required: true,
      type: Array,
    },
  },
  data () {
    return {
      weightList: [],
      groupsClone: [],
      currentPreferenceName: '',
      display: true,
      baseSelector: null,
    }
  },
  watch: {
    groupsClone: {
      deep: true,
      handler (newValue) {
        this.$emit('input', {
          name: this.currentPreferenceName,
          groups: newValue,
          index: this.value.index,
          base_selector: this.value.base_selector,
        })
      },
    },
    value: {
      deep: true,
      handler (newValue) {
        if (this.value.base_selector) {
          this.baseSelector = newValue.base_selector
        }
      },
    },
  },
  created () {
    this.currentPreferenceName = this.preferenceName
    this.weightList = this.createWeightList(this.value.groups)
    this.groupsClone = _cloneDeep(this.value.groups)
  },
  mounted () {
    if (this.value.base_selector) {
      this.baseSelector = this.value.base_selector
    }
  },
  methods: {
    // #region Create weight list
    /**
     * This function is used, because not each selector group needs to have the same weighted selectors.
     * To make sure that each weight row is displayed
     */
    createWeightList (groups) {
      let allGroupWeights = []
      for (let i = 0, length = groups.length; i < length; i++) {
        allGroupWeights = allGroupWeights.concat(groups[i].selectors.map(selector => selector.weight))
      }
      return [...new Set(allGroupWeights)].sort().reverse()
    },
    // #endregion
    // #region Preference
    update ({ newPreferenceName, oldPreferenceName, baseSelector }) {
      this.$emit('Update', { newPreferenceName, oldPreferenceName, baseSelector })
    },
    remove (preferenceName) {
      for (let i = 0, length = this.value.groups.length; i < length; i++) {
        this.$store.dispatch('delete_group', { name: this.value.groups[i].name, term: this.value.groups[i].term })
      }
      this.$emit('Remove', preferenceName)
    },
    copy (preferenceName) {
      const groups = _cloneDeep(this.groupsClone)
      this.$emit('Copy', {
        preferenceName: preferenceName + ' ' + crypto.randomUUID().substring(0, 8),
        groups,
      })
      toast.info(Lang.trans('subscription.classification_config.classification_config_copy'))
    },
    // #endregion
    // #region Group
    addGroup ({ name, max, term }) {
      this.groupsClone.push(
        {
          name,
          max,
          term,
          selectors: [],
        },
      )
    },
    removeGroup (index) {
      this.groupsClone.splice(index, 1)
    },
    copyGroup (group) {
      this.groupsClone.push(group)
    },
    // #endregion
  },
}
</script>

<style scoped>
.visibility-collapse {
  visibility: collapse;
}
/deep/
.height-cell {
  height: 61px;
}
.preference-config {
  overflow-x: auto;
}
/deep/
.height-header-cell {
  height: 37px;
}
.overflow-x {
  overflow-x: auto;
}
.overflow-x::-webkit-scrollbar {
  height: 10px;
}
.overflow-x::-webkit-scrollbar-track {
  background:#E4E2E0;
}
.overflow-x::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
}
table {
  display: table;
  white-space:nowrap;
}
/deep/
table tr {
  display: inline-flex;
  flex-direction: column;
}
/deep/
table tr td {
  border-bottom: 1pt solid black !important;
  border-left: 0.5px solid rgba(0, 0, 0, 0.2);
  border-right: 0.5px solid rgba(0, 0, 0, 0.2);
}
tr td:last-child {
  border-left: none;
  border-right: none;
  border-bottom: none;
}
/deep/
table tr th {
  border-bottom: 1pt solid black;
  border-left: 1px solid rgba(0, 0, 0, 0.2);
  border-right: 1px solid rgba(0, 0, 0, 0.2);
  height: 37px;
}
/deep/
.small-button-width {
  width: 1.75rem;
}
/deep/
.btn-warning{
  background: #FFC30E !important;
  color: white !important;
}
.border-right-none {
  border-right: none;
}
</style>
